import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import { AgentModel } from './agent.model';
import type { ActionTrackingContext } from '../actions/framework/action-tracking';

type Params = {
  transcribedText: string;
  chatRef: React.RefObject<{
    appendInputValue: (value: string) => void;
  }>;
};

export class VoiceTranscriptionCompleteAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Voice Transcription Complete';
  description = 'Handle completed voice transcription for agent chat';
  inputSchema = z.object({
    transcribedText: z.string(),
    chatRef: z.custom<
      React.RefObject<{
        appendInputValue: (value: string) => void;
      }>
    >(),
  });
  discrete = true;

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(AgentModel);
  }

  async execute(
    params: Params,
    _context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    if (params.chatRef.current !== null) {
      params.chatRef.current.appendInputValue(params.transcribedText);
    }

    return ok({});
  }

  track({
    payload,
    context,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const agent = context.targetedModelOfType(AgentModel);
    if (agent === null) {
      return null;
    }

    return {
      event: 'agent_voice_transcription_complete',
      properties: {
        agentId: agent.id,
        agentName: agent.name.snapshot(),
        textLength: payload.transcribedText.length,
        hasContent: payload.transcribedText.trim().length > 0,
        currentTab: agent.currentTab.snapshot(),
        hasMessages: agent.hasMessages.snapshot(),
      },
    };
  }
}
